// ScoringConfigEditor.tsx - Form for creating/editing scoring configurations

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  ScoringConfiguration, 
  BaseScoreConfig, 
  ScoringWeights,
  ResponseTimeBonusConfig,
  GradeThreshold
} from '../../types/ScoringConfiguration';
import { ScoringConfigurationService } from '../../services/ScoringConfigurationService';
import { StandardCard, StandardButton, FormField, TextAreaField } from '../ui';

interface ScoringConfigEditorProps {
  configuration: ScoringConfiguration | null;
  mode: 'create' | 'edit';
  onSave: (config: ScoringConfiguration) => void;
  onCancel: () => void;
}

export const ScoringConfigEditor: React.FC<ScoringConfigEditorProps> = ({
  configuration,
  mode,
  onSave,
  onCancel
}) => {
  const { isDark } = useTheme();
  
  // Initialize form state
  const [formData, setFormData] = useState<ScoringConfiguration>(() => {
    if (configuration) {
      return { ...configuration };
    }
    
    const defaultConfig = ScoringConfigurationService.getDefaultConfiguration();
    return {
      ...defaultConfig,
      id: `custom-${Date.now()}`,
      name: '',
      description: '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      version: 1
    };
  });

  const [errors, setErrors] = useState<string[]>([]);
  const [activeSection, setActiveSection] = useState<string>('basic');

  // Validate form on changes
  useEffect(() => {
    const validation = ScoringConfigurationService.validate(formData);
    setErrors(validation.errors);
  }, [formData]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBaseScoreChange = (category: keyof BaseScoreConfig, value: number) => {
    setFormData(prev => ({
      ...prev,
      baseScores: {
        ...prev.baseScores,
        [category]: Math.max(0, Math.min(100, value))
      }
    }));
  };

  const handleWeightChange = (category: keyof ScoringWeights, value: number) => {
    const newValue = Math.max(0, Math.min(1, value));
    setFormData(prev => ({
      ...prev,
      categoryWeights: {
        ...prev.categoryWeights,
        [category]: newValue
      }
    }));
  };

  const handleResponseTimeBonusChange = (field: keyof ResponseTimeBonusConfig, value: number) => {
    setFormData(prev => ({
      ...prev,
      responseTimeBonus: {
        ...prev.responseTimeBonus,
        [field]: value
      }
    }));
  };

  const handleGradeThresholdChange = (index: number, field: keyof GradeThreshold, value: any) => {
    setFormData(prev => {
      const newThresholds = [...prev.gradeThresholds];
      newThresholds[index] = {
        ...newThresholds[index],
        [field]: value
      };
      return {
        ...prev,
        gradeThresholds: newThresholds
      };
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (errors.length > 0) {
      return;
    }

    onSave(formData);
  };

  const sections = [
    { id: 'basic', label: 'Basic Info', icon: '📝' },
    { id: 'scores', label: 'Base Scores', icon: '🎯' },
    { id: 'weights', label: 'Category Weights', icon: '⚖️' },
    { id: 'timing', label: 'Response Time', icon: '⏱️' },
    { id: 'grades', label: 'Grade Thresholds', icon: '📊' },
    { id: 'advanced', label: 'Advanced Settings', icon: '⚙️' }
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Section Navigation */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {sections.map(section => (
          <StandardButton
            key={section.id}
            type="button"
            variant={activeSection === section.id ? 'primary' : 'secondary'}
            size="small"
            onClick={() => setActiveSection(section.id)}
            leftIcon={section.icon}
          >
            {section.label}
          </StandardButton>
        ))}
      </div>

      {/* Error Display */}
      {errors.length > 0 && (
        <div className={`rounded-lg p-4 ${isDark ? 'bg-red-900/20 border border-red-500/50' : 'bg-red-50 border border-red-200'}`}>
          <div className="space-y-1">
            <p className={`font-medium ${isDark ? 'text-red-400' : 'text-red-700'}`}>Please fix the following errors:</p>
            {errors.map((error, index) => (
              <p key={index} className={`text-sm ${isDark ? 'text-red-300' : 'text-red-600'}`}>{error}</p>
            ))}
          </div>
        </div>
      )}

      {/* Form Sections */}
      <motion.div
        key={activeSection}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
      >
        {activeSection === 'basic' && (
          <StandardCard variant="outlined" padding="large">
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Basic Information
            </h3>
            <div className="space-y-4">
              <FormField
                label="Configuration Name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="e.g., Balanced Scoring"
                required
              />
              <TextAreaField
                label="Description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Describe the purpose of this configuration"
                rows={3}
              />
            </div>
          </StandardCard>
        )}

        {activeSection === 'scores' && (
          <StandardCard variant="outlined" padding="large">
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Base Scores (0-100)
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(formData.baseScores).map(([category, value]) => (
                <div key={category}>
                  <label className={`block text-sm font-medium mb-2 capitalize ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {category}
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={value}
                    onChange={(e) => handleBaseScoreChange(
                      category as keyof BaseScoreConfig,
                      parseInt(e.target.value) || 0
                    )}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      isDark 
                        ? 'bg-gray-800 border-gray-700 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                </div>
              ))}
            </div>
          </StandardCard>
        )}

        {activeSection === 'weights' && (
          <StandardCard variant="outlined" padding="large">
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Category Weights (Must sum to 1.0)
            </h3>
            <div className="space-y-4">
              {Object.entries(formData.categoryWeights).map(([category, value]) => (
                <div key={category}>
                  <div className="flex justify-between items-center mb-2">
                    <label className={`text-sm font-medium capitalize ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      {category}
                    </label>
                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {(value * 100).toFixed(0)}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={value}
                    onChange={(e) => handleWeightChange(
                      category as keyof ScoringWeights,
                      parseFloat(e.target.value)
                    )}
                    className="w-full"
                  />
                </div>
              ))}
              <div className={`text-sm mt-2 ${
                Math.abs(Object.values(formData.categoryWeights).reduce((a, b) => a + b, 0) - 1.0) > 0.01
                  ? 'text-red-500'
                  : isDark ? 'text-green-400' : 'text-green-600'
              }`}>
                Total: {(Object.values(formData.categoryWeights).reduce((a, b) => a + b, 0) * 100).toFixed(0)}%
              </div>
            </div>
          </StandardCard>
        )}

        {activeSection === 'timing' && (
          <StandardCard variant="outlined" padding="large">
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Response Time Bonuses
            </h3>
            <div className="space-y-6">
              <div>
                <h4 className={`font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Excellent Response
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    label="Threshold (% of time limit)"
                    type="number"
                    min="0"
                    max="1"
                    step="0.1"
                    value={formData.responseTimeBonus.excellentThreshold}
                    onChange={(e) => handleResponseTimeBonusChange('excellentThreshold', parseFloat(e.target.value) || 0)}
                  />
                  <FormField
                    label="Bonus Points"
                    type="number"
                    min="0"
                    max="20"
                    value={formData.responseTimeBonus.excellentBonus}
                    onChange={(e) => handleResponseTimeBonusChange('excellentBonus', parseInt(e.target.value) || 0)}
                  />
                </div>
              </div>

              <div>
                <h4 className={`font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Good Response
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    label="Threshold (% of time limit)"
                    type="number"
                    min="0"
                    max="1"
                    step="0.1"
                    value={formData.responseTimeBonus.goodThreshold}
                    onChange={(e) => handleResponseTimeBonusChange('goodThreshold', parseFloat(e.target.value) || 0)}
                  />
                  <FormField
                    label="Bonus Points"
                    type="number"
                    min="0"
                    max="20"
                    value={formData.responseTimeBonus.goodBonus}
                    onChange={(e) => handleResponseTimeBonusChange('goodBonus', parseInt(e.target.value) || 0)}
                  />
                </div>
              </div>
            </div>
          </StandardCard>
        )}

        {activeSection === 'grades' && (
          <StandardCard variant="outlined" padding="large">
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Grade Thresholds
            </h3>
            <div className="space-y-3">
              {formData.gradeThresholds.map((threshold, index) => (
                <div key={index} className="grid grid-cols-3 gap-3">
                  <FormField
                    label={index === 0 ? "Min Score" : ""}
                    type="number"
                    min="0"
                    max="100"
                    value={threshold.minScore}
                    onChange={(e) => handleGradeThresholdChange(index, 'minScore', parseInt(e.target.value) || 0)}
                  />
                  <FormField
                    label={index === 0 ? "Grade" : ""}
                    value={threshold.grade}
                    onChange={(e) => handleGradeThresholdChange(index, 'grade', e.target.value)}
                  />
                  <FormField
                    label={index === 0 ? "Description" : ""}
                    value={threshold.description}
                    onChange={(e) => handleGradeThresholdChange(index, 'description', e.target.value)}
                  />
                </div>
              ))}
            </div>
          </StandardCard>
        )}

        {activeSection === 'advanced' && (
          <StandardCard variant="outlined" padding="large">
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Advanced Settings
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Enable Investment Synergies
                </label>
                <input
                  type="checkbox"
                  checked={formData.enableSynergies}
                  onChange={(e) => handleInputChange('enableSynergies', e.target.checked)}
                  className="w-4 h-4"
                />
              </div>

              <div className="flex items-center justify-between">
                <label className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Enable Response Time Bonus
                </label>
                <input
                  type="checkbox"
                  checked={formData.enableResponseTimeBonus}
                  onChange={(e) => handleInputChange('enableResponseTimeBonus', e.target.checked)}
                  className="w-4 h-4"
                />
              </div>

              <FormField
                label="Difficulty Multiplier"
                type="number"
                min="0.5"
                max="2.0"
                step="0.1"
                value={formData.difficultyMultiplier}
                onChange={(e) => handleInputChange('difficultyMultiplier', parseFloat(e.target.value) || 1.0)}
                helperText="1.0 = Normal, <1.0 = Easier, >1.0 = Harder"
              />

              <FormField
                label="Max Incident Mitigation"
                type="number"
                min="0"
                max="1"
                step="0.05"
                value={formData.maxIncidentMitigation}
                onChange={(e) => handleInputChange('maxIncidentMitigation', parseFloat(e.target.value) || 0.5)}
                helperText="Maximum mitigation from investments (0-1)"
              />

              <FormField
                label="Max Response Time Improvement"
                type="number"
                min="0"
                max="1"
                step="0.05"
                value={formData.maxResponseTimeImprovement}
                onChange={(e) => handleInputChange('maxResponseTimeImprovement', parseFloat(e.target.value) || 0.4)}
                helperText="Maximum time improvement from investments (0-1)"
              />
            </div>
          </StandardCard>
        )}
      </motion.div>

      {/* Form Actions */}
      <div className="flex justify-end gap-3">
        <StandardButton
          type="button"
          variant="secondary"
          onClick={onCancel}
        >
          Cancel
        </StandardButton>
        <StandardButton
          type="submit"
          variant="primary"
          disabled={errors.length > 0 || !formData.name}
        >
          {mode === 'create' ? 'Create Configuration' : 'Save Changes'}
        </StandardButton>
      </div>
    </form>
  );
};